import React from "react";
import { ArticleLayout, H2, H3, Quote, Table } from "./ArticleLayout";

const COMPARISON_HEADERS = ["Critério", "Run-Flat", "Pneu Normal + Kit"];
const COMPARISON_ROWS = [
  ["Segurança pós-furo", "Continua a rolar até 80 km", "Imobiliza o veículo"],
  ["Conforto de rolagem", "Mais firme, transmite buracos", "Mais macio e absorvente"],
  ["Ruído no habitáculo", "Tipicamente +2 a 4 dB", "Mais silencioso"],
  ["Peso por pneu", "+1,5 a 2 kg", "Padrão"],
  ["Preço unitário", "+30% a +50%", "Mais acessível"],
  ["Reparável após furo", "Sim (após avaliação técnica na oficina)", "Sim (na banda de rolamento)"],
  ["Disponibilidade em stock", "Limitada a medidas premium", "Ampla"],
];

export const RunFlatBMW = () => {
  return (
    <ArticleLayout
      testId="article-run-flat-bmw"
      tag="BMW · Premium"
      title="O melhor pneu para o meu BMW: Run-Flat ou pneu normal?"
      readTime="6 min"
      image="/tires/michelin-primacy-5.png"
      intro="Se conduz um BMW Série 3, Série 5, X3 ou X5 fabricado nos últimos 15 anos, provavelmente veio equipado de fábrica com pneus Run-Flat. Esses pneus permitem rodar até 80 km a no máximo 80 km/h mesmo depois de furar — mas serão a melhor opção quando chega a hora de substituir? Neste guia, comparamos lado a lado."
    >
      <H2>O que são pneus Run-Flat</H2>
      <p>
        Os pneus Run-Flat (também chamados RFT ou ZP, dependendo do
        fabricante) têm flancos reforçados com borracha rígida adicional. Em
        caso de furo ou perda total de pressão, esses flancos suportam o peso
        do veículo durante uma distância limitada, permitindo-lhe chegar a
        casa ou a uma oficina sem ter de trocar de pneu no acostamento.
      </p>
      <p>
        Marcas como BMW, Mini, Mercedes-AMG e algumas Audi RS optam por
        equipar de origem este tipo de pneu — geralmente eliminando o pneu
        sobressalente para ganhar espaço na bagageira e reduzir peso.
        Reconhece-os pelas marcações na parede lateral:{" "}
        <strong>RFT</strong> (Bridgestone/Goodyear),{" "}
        <strong>ZP</strong> (Michelin),{" "}
        <strong>SSR</strong> (Continental),{" "}
        <strong>HRS</strong> (Hankook) ou{" "}
        <strong>RSC</strong> (BMW spec).
      </p>

      <H2>Run-Flat vs Pneu Normal: comparação direta</H2>
      <Table
        headers={COMPARISON_HEADERS}
        rows={COMPARISON_ROWS}
      />
      <p className="text-sm md:text-base leading-relaxed mt-4 p-4 border-l-2 border-[#FF5A00] bg-[#0E2452]/40">
        <strong>Nota importante:</strong> ao contrário do que se costuma
        dizer, um pneu Run-Flat com furo <strong>pode ser reparado</strong>{" "}
        na maior parte dos casos — desde que o dano esteja na banda de
        rolamento, a distância percorrida sem pressão seja curta e a
        estrutura interna não tenha sido comprometida. Na Global Pneus
        fazemos essa avaliação técnica sem custos e indicamos a solução
        mais segura para cada situação.
      </p>

      <H2>Posso passar do Run-Flat para pneu normal no meu BMW?</H2>
      <p>
        <strong>Sim, é tecnicamente possível</strong> — e legal — desde que
        respeite duas condições:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Compre um kit de reparação ou um pneu sobressalente
          compacto.</strong> Sem Run-Flat, perderá a capacidade de continuar
          a rolar após um furo. Os modelos BMW recentes têm um espaço para
          alojar um pneu sobressalente compacto opcional (peça original BMW).
        </li>
        <li>
          <strong>Mantenha o sensor TPMS funcional.</strong> Todos os BMWs
          equipados com Run-Flat têm sistema de monitorização da pressão dos
          pneus (TPMS) — é obrigatório por lei manter esse sistema operativo
          mesmo se mudar para pneus normais. Os pneus modernos são
          compatíveis sem alterações.
        </li>
      </ul>

      <Quote>
        Em cerca de 70% dos casos que vemos na oficina, o cliente BMW que
        pediu para passar para pneu normal fica mais satisfeito — sobretudo
        pelo ganho de conforto.
      </Quote>

      <H2>Quais os melhores pneus para BMW?</H2>
      <H3>Se mantiver Run-Flat (recomendado para BMW M ou utilização desportiva)</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Michelin Pilot Sport 4 ZP</strong> — referência absoluta em
          desportividade, equilibra agarre e durabilidade.
        </li>
        <li>
          <strong>Bridgestone Potenza Sport RFT</strong> — equipamento de
          origem em vários BMW Série M.
        </li>
        <li>
          <strong>Continental SportContact 7 SSR</strong> — excelente
          equilíbrio entre conforto e travagem em molhado.
        </li>
      </ul>

      <H3>Se mudar para pneu normal (recomendado para uso diário)</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Michelin Primacy 5</strong> — a referência em conforto,
          baixo ruído e durabilidade. A nossa escolha mais recomendada para
          BMW Série 3, 5 e X3 de utilização urbana/estrada.
        </li>
        <li>
          <strong>Hankook Ventus S1 evo³ (K127)</strong> — pneu premium com
          tecnologia <em>Sound Absorber</em> (espuma interna de
          poliuretano). Aprovado de origem em alguns BMW e Mercedes.
        </li>
        <li>
          <strong>Continental PremiumContact 7</strong> — vencedor de vários
          testes ADAC, excelente em condições mistas.
        </li>
      </ul>

      <H2>Veredito final</H2>
      <p>
        Se conduz o seu BMW essencialmente em estradas urbanas e quer ganhar
        conforto + poupar até 30% no preço dos pneus, mudar para pneus
        normais (com um kit de reparação) é uma decisão racional. Se faz
        muitas viagens longas em autoestrada e prioriza a segurança absoluta
        em caso de furo, mantenha Run-Flat.
      </p>
      <p>
        Na Global Pneus aconselhamos consoante o seu modelo, ano e estilo de
        condução. Traga o livrete e nós validamos a compatibilidade exata.
      </p>
    </ArticleLayout>
  );
};

export default RunFlatBMW;
